<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { dismiss, pause, resume, toasts } from '../stores/toast';

const { t } = useI18n();
async function act(id: number, run: () => void | Promise<void>): Promise<void> {
  dismiss(id);
  await run();
}
</script>

<template>
  <div
    class="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex w-full max-w-md -translate-x-1/2 flex-col gap-2 px-4"
    aria-live="polite"
  >
    <TransitionGroup
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-2 opacity-0"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-for="tt in toasts"
        :key="tt.id"
        class="pointer-events-auto relative overflow-hidden rounded-lg text-sm text-white shadow-[0_10px_30px_-5px_rgba(0,0,0,0.45),0_4px_10px_-2px_rgba(0,0,0,0.3)] ring-1 ring-white/15"
        :class="tt.tone === 'error' ? 'bg-red-600' : tt.tone === 'info' ? 'bg-indigo-600' : tt.tone === 'warning' ? 'bg-amber-600' : 'bg-emerald-600'"
        role="status"
        @mouseenter="pause(tt.id)"
        @mouseleave="resume(tt.id)"
        @focusin="pause(tt.id)"
        @focusout="resume(tt.id)"
      >
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
          <span v-if="tt.tone === 'success'" class="text-white" aria-hidden="true">✓</span>
          <span v-else-if="tt.tone === 'warning'" class="text-white" aria-hidden="true">⚠</span>
          <span class="flex-1">{{ tt.message }}</span>
          <button v-if="tt.action" type="button" class="font-semibold text-white underline underline-offset-2 hover:text-white/80" @click="act(tt.id, tt.action.run)">
            {{ tt.action.label }}
          </button>
          <button
            v-for="a in tt.actions ?? []"
            :key="a.label"
            type="button"
            class="rounded bg-white/15 px-2 py-0.5 font-semibold text-white hover:bg-white/25"
            @click="act(tt.id, a.run)"
          >
            {{ a.label }}
          </button>
          <button type="button" class="text-white/70 hover:text-white" :aria-label="t('toast.dismiss')" @click="dismiss(tt.id)">✕</button>
        </div>
        <!-- Time left: drains to nothing, then the toast goes (paused while hovered). -->
        <div class="absolute inset-x-0 bottom-0 h-1 bg-black/15" aria-hidden="true">
          <div
            class="toast-bar h-full origin-left bg-white/80 rtl:origin-right"
            :style="{ animationDuration: `${tt.ms}ms`, animationPlayState: tt.paused ? 'paused' : 'running' }"
          />
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-bar {
  animation-name: toast-drain;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
@keyframes toast-drain {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .toast-bar {
    animation-timing-function: steps(4);
  }
}
</style>
